import { useCallback, useRef, useState } from 'react'

import { sfx } from '../../../lib/sound'
import { TouchPad, useDirectionKeys, useGameLoop, type GameProps } from '../GameShell'

const SIZE = 15
const START_SPEED = 180
const MIN_SPEED = 70

type Point = { x: number; y: number }
type Dir = 'up' | 'down' | 'left' | 'right'

const STEP: Record<Dir, Point> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
}

const OPPOSITE: Record<Dir, Dir> = {
  up: 'down',
  down: 'up',
  left: 'right',
  right: 'left',
}

function randomFood(snake: Point[]): Point {
  // Pick from the free cells rather than guessing repeatedly: on a nearly full
  // board, rejection sampling can spin for a very long time.
  const taken = new Set(snake.map((p) => `${p.x},${p.y}`))
  const free: Point[] = []
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (!taken.has(`${x},${y}`)) free.push({ x, y })
    }
  }
  return free[Math.floor(Math.random() * free.length)] ?? { x: 0, y: 0 }
}

export default function Snake({ onGameOver, onScore }: GameProps) {
  const [snake, setSnake] = useState<Point[]>([
    { x: 7, y: 7 },
    { x: 6, y: 7 },
  ])
  const [food, setFood] = useState<Point>({ x: 11, y: 7 })
  const [score, setScore] = useState(0)
  const [alive, setAlive] = useState(true)

  const direction = useRef<Dir>('right')
  // The direction actually used by the last tick. Turning twice within one tick
  // could otherwise reverse the snake into itself.
  const applied = useRef<Dir>('right')

  const turn = useCallback((next: Dir) => {
    if (next === OPPOSITE[applied.current]) return
    direction.current = next
  }, [])

  useDirectionKeys(turn, alive)

  const speed = Math.max(MIN_SPEED, START_SPEED - score * 4)

  const tick = useCallback(() => {
    setSnake((current) => {
      const dir = direction.current
      applied.current = dir
      const head = current[0]
      const next = { x: head.x + STEP[dir].x, y: head.y + STEP[dir].y }

      const hitWall =
        next.x < 0 || next.y < 0 || next.x >= SIZE || next.y >= SIZE
      // The tail tip moves away this tick, so it is not a collision.
      const hitSelf = current
        .slice(0, -1)
        .some((p) => p.x === next.x && p.y === next.y)

      if (hitWall || hitSelf) {
        setAlive(false)
        onGameOver(score)
        return current
      }

      const ate = next.x === food.x && next.y === food.y
      const grown = [next, ...current]
      if (!ate) grown.pop()
      else {
        sfx.pop()
        setFood(randomFood(grown))
        setScore((s) => {
          const updated = s + 1
          onScore?.(updated)
          return updated
        })
      }
      return grown
    })
  }, [food, onGameOver, onScore, score])

  useGameLoop(tick, speed, alive)

  const cells = new Map(snake.map((p, i) => [`${p.x},${p.y}`, i]))

  return (
    <div>
      <div
        className="mx-auto grid aspect-square w-full max-w-md gap-px rounded-xl bg-border-subtle p-px"
        style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: SIZE * SIZE }, (_, i) => {
          const x = i % SIZE
          const y = Math.floor(i / SIZE)
          const part = cells.get(`${x},${y}`)
          const isFood = food.x === x && food.y === y

          return (
            <div
              key={i}
              className={`rounded-[2px] ${
                part === 0
                  ? 'bg-brand'
                  : part !== undefined
                    ? 'bg-brand/60'
                    : isFood
                      ? 'bg-positive'
                      : 'bg-surface'
              }`}
            />
          )
        })}
      </div>
      <TouchPad onDirection={turn} />
    </div>
  )
}
